import { xMinMaxValues } from 'ml-spectra-processing';
import { getData } from './getData.js';
import { getNormalized } from './getNormalized.js';
import { updateRangesInfo } from './updateRangesInfo.js';
/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param x - x values array
 * @param y - y values array
 * @param id - spectrum identifier
 * @param options - initialization options
 */
export class Spectrum {
    x;
    y;
    id;
    meta;
    normalizedBoundary;
    normalized;
    normalizedAllowedBoundary;
    memory;
    ranges;
    /**
     * Constructor for Spectrum
     * @param x - x values array
     * @param y - y values array
     * @param id - spectrum identifier
     * @param options - initialization options with meta, normalization, and normalized properties
     */
    constructor(x, y, id, options = {}) {
        const { meta = {}, normalization = {}, normalized } = options;
        if (x && x.length > 1 && x[0] > x.at(-1)) {
            this.x = x.toReversed();
            this.y = y.toReversed();
        }
        else {
            this.x = x || [];
            this.y = y || [];
        }
        this.id = id;
        this.meta = meta;
        this.normalizedBoundary = { x: { min: 0, max: 0 }, y: { min: 0, max: 0 } };
        if (normalized) {
            this.normalized = normalized;
            this.updateNormalizedBoundary();
            this.normalizedAllowedBoundary = this.normalizedBoundary;
        }
        else {
            this.normalized = this.updateNormalization(normalization);
        }
        this.updateMemory();
    }
    /**
     * Get the minimum x value
     */
    get minX() {
        return this.x[0];
    }
    /**
     * Get the maximum x value
     */
    get maxX() {
        return this.x.at(-1);
    }
    /**
     * Update memory usage statistics
     */
    updateMemory() {
        const original = (this.x?.length || 0) * 16;
        const normalizedMem = (this.normalized?.x?.length || 0) * 16;
        this.memory = {
            original,
            normalized: normalizedMem,
            total: original + normalizedMem,
        };
    }
    /**
     * Remove original x and y data
     */
    removeOriginal() {
        this.x = undefined;
        this.y = undefined;
        this.updateMemory();
    }
    /**
     * Get x and y arrays
     * @returns Object containing x and y arrays
     * @throws Error if x or y is not an array
     */
    getXY() {
        if (!Array.isArray(this.x) || !Array.isArray(this.y)) {
            throw new Error('Can not get normalized data');
        }
        return { x: this.x, y: this.y };
    }
    /**
     * Update ranges information
     * @param ranges - ranges configuration
     */
    updateRangesInfo(ranges) {
        updateRangesInfo(this, ranges);
    }
    /**
     * Update normalization
     * @param normalization - normalization configuration
     */
    updateNormalization(normalization) {
        const result = getNormalized(this, normalization);
        this.normalized = result.data;
        this.normalizedAllowedBoundary = result.allowedBoundary;
        this.ranges = {};
        this.updateMemory();
        this.updateNormalizedBoundary();
        return result.data;
    }
    /**
     * Get spectrum data with optional filtering and scaling
     * @param options - options for data retrieval
     * @returns Data object with x and y arrays
     */
    getData(options) {
        return getData(this, options);
    }
    /**
     * Update normalized boundary values
     */
    updateNormalizedBoundary() {
        this.normalizedBoundary.x = {
            min: this.normalized.x[0],
            max: this.normalized.x.at(-1),
        };
        this.normalizedBoundary.y = xMinMaxValues(this.normalized.y);
    }
}
//# sourceMappingURL=Spectrum.js.map